import { useCallback, useState } from "react"
import { useIntl } from "react-intl"

import { Button, Dialog, StaffBodyTextXXS, StaffLabelTextS } from "@doktor-se/bones-ui"
import { Avatar, SymptomBubble } from "@doktor-se/bones-ui/dist/web-shared/components"
import { AssignedConversation, Category } from "@doktor-se/bones-ui/dist/web-shared/types"

import { appStatic } from "config"
import { useAppSelector } from "lib/hooks"
import { clinicSelectors } from "reducers/clinics"

import { SearchCategoriesWrapper } from "components/searchList"
import { Offset } from "pages/dashboard/components/ConversationControls/ConversationControls"

import styles from "./RedirectDialog.module.scss"

interface RedirectDialogProps {
  isOpen: boolean
  offset: Offset
  conversation: AssignedConversation
  onClose: () => void
  onCancel: () => void
  onConfirm: (categoryKey: string) => void
}

const RedirectDialog = ({ isOpen, offset, conversation, onClose, onCancel, onConfirm }: RedirectDialogProps) => {
  const [selectedCategory, setSelectedCategory] = useState<Category>()
  const [currentStep, setCurrentStep] = useState<1 | 2>(1)
  const [isSelectCategoryError, setIsSelectCategoryError] = useState(false)
  const clinics = useAppSelector(clinicSelectors.selectAll)
  const user = useAppSelector(state => state.auth.user)
  const userClinicName = clinics.find(c => c.id === user?.data?.clinic)?.name

  const intl = useIntl()
  const onConfirmClick = () => {
    if (!selectedCategory) {
      setIsSelectCategoryError(true)
      return
    }
    onConfirm(selectedCategory.key)
  }
  const additionalCategoriesFilter = useCallback(
    (categories: Category[]) => {
      if (!userClinicName) return categories.filter(c => c.id !== conversation.categoryId)

      let clinicIdentifier = userClinicName
        .replace(/\s+(VC|hälsocentral|vårdcentral|läkarmottagning|mottagning|vårdcentral och BVC)$/i, "")
        .trim()
      const identifierVariations = [clinicIdentifier]

      if (clinicIdentifier.endsWith("s")) {
        identifierVariations.push(clinicIdentifier.slice(0, -1))
      }

      if (!clinicIdentifier.endsWith("s")) {
        identifierVariations.push(clinicIdentifier + "s")
      }

      const filteredCategories = categories.filter(
        c => identifierVariations.some(variation => c.name.includes(variation)) && c.id !== conversation.categoryId
      )
      return filteredCategories
    },
    [userClinicName, conversation.categoryId]
  )

  const handleCategoryChange = (category: Category) => {
    setSelectedCategory(category)
    setCurrentStep(1)
  }

  const handleSelectCategoryClick = () => {
    setCurrentStep(2)
  }

  const renderStep1 = () => (
    <>
      <div className={styles.patientInfo}>
        <div className={styles.patientInfoItem}>
          <StaffLabelTextS className={styles.patientInfoHeading}>
            {intl.formatMessage({ id: "redirectDialog.patient" })}
          </StaffLabelTextS>
          <div className={styles.patientInfoValue}>
            <Avatar />
            {conversation.account?.displayName}
          </div>
        </div>
        <div className={styles.patientInfoItem}>
          <StaffLabelTextS className={styles.patientInfoHeading}>
            {intl.formatMessage({ id: "redirectDialog.category" })}
          </StaffLabelTextS>
          <div className={styles.patientInfoValue}>
            {selectedCategory ? (
              <div className={styles.categoryRow}>
                {selectedCategory.imageUrl && <SymptomBubble symptom={selectedCategory} appStatic={appStatic} />}
                <span className={styles.categoryName}>{selectedCategory.name}</span>
                <Button color="primary" variant="outline" onPress={handleSelectCategoryClick}>
                  {intl.formatMessage({ id: "redirectDialog.change" })}
                </Button>
              </div>
            ) : (
              <div className={styles.selectCategoryButton}>
                <Button
                  color={isSelectCategoryError ? "accent" : "primary"}
                  variant="outline"
                  onPress={handleSelectCategoryClick}
                  fullWidth>
                  {intl.formatMessage({ id: "redirectDialog.selectCategory" })}
                </Button>
                {isSelectCategoryError && (
                  <StaffBodyTextXXS className={styles.selectCategoryMessage}>
                    {intl.formatMessage({ id: "redirectDialog.selectCategoryMessage" })}
                  </StaffBodyTextXXS>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.divider} />
      <div className={styles.info}>
        <StaffBodyTextXXS>{intl.formatMessage({ id: "redirectDialog.info" })}</StaffBodyTextXXS>
      </div>
      <div className={styles.actions}>
        <Button color="primary" variant="secondary" onPress={onCancel}>
          {intl.formatMessage({ id: "redirectDialog.cancel" })}
        </Button>
        <Button color="primary" variant="primary" onPress={onConfirmClick}>
          {intl.formatMessage({ id: "redirectDialog.create" })}
        </Button>
      </div>
    </>
  )

  const renderStep2 = () => (
    <>
      <div className={styles.content}>
        <div className={styles.wrapper}>
          <SearchCategoriesWrapper
            onSelectCategory={handleCategoryChange}
            filterCategoriesForProfile={false}
            additionalCategoriesFilter={additionalCategoriesFilter}
            showSubcategoriesHeaders={false}
          />
        </div>
      </div>
    </>
  )

  return (
    <Dialog
      isOpen={isOpen}
      offset={offset && { top: `${offset.top}px`, left: `${offset.left}px` }}
      closeAriaLabel="close"
      onClose={onClose}
      fixedWidth
      disableHorizontalScroll
      contentFullWidth
      variant="blue"
      headingText={intl.formatMessage({ id: "redirectDialog.heading" })}
      subheadingText={intl.formatMessage({ id: "redirectDialog.subheading" })}>
      {currentStep === 1 ? renderStep1() : renderStep2()}
    </Dialog>
  )
}

export default RedirectDialog
